import dayjs from 'dayjs';
import { useState } from 'react';
import Alert from '@liquidweb-dev/undercurrent/Alert';
import Button from '@liquidweb-dev/undercurrent/Button';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import ArrowLeftIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeft';
import HookedSubmit from 'components/Form/HookedSubmit';
import HookedTextField from 'components/Form/HookedTextField';
import LWForm from 'components/LWForm';
import { type Validator } from 'components/LWForm';
import DestroyNote from 'components/DialogDestroyAddon/DestroyNote';
import { dateMonthYear } from 'utility/constants/dateTime';

export const DESTROY_FORM_VALUES = {
	verification_text: '',
};

export const DESTROY_FORM_ERRORS = {
	verification_text: '',
};

export const DESTROY_FORM_VALIDATOR: Record<string, Validator> = {
	verification_text: (value) => {
		if (value !== 'DESTROY') return 'Please type DESTROY to confirm';
		return '';
	},
};

export interface DestroyConfirmationProps {
	uniqId: string;
	addonName: string;
	onBack: () => void;
	onDestroy?: () => void;
	isLoading?: boolean;
}

const DestroyConfirmation = ({
	addonName,
	onBack,
	onDestroy,
	isLoading,
}: DestroyConfirmationProps) => {
	const [formValues, setFormValues] = useState(DESTROY_FORM_VALUES);
	const [formErrors, setFormErrors] = useState(DESTROY_FORM_ERRORS);

	return (
		<LWForm
			values={formValues}
			onValuesChange={setFormValues}
			errors={formErrors}
			validators={DESTROY_FORM_VALIDATOR}
			onErrorsChange={setFormErrors}
			onSubmit={onDestroy}
		>
			<Stack sx={{ gap: 200, width: '100%' }}>
				<Stack sx={{ gap: 300, width: '100%' }}>
					<Alert severity="error" showIcon={false}>
						<Typography
							component="span"
							variant="body-sm"
							sx={{ fontWeight: 400 }}
						>
							This will permanently remove{' '}
						</Typography>
						<Typography
							component="span"
							variant="body-sm"
							sx={{ fontWeight: 700 }}
						>
							{addonName}
						</Typography>
						{'. '}
						<Typography
							component="span"
							variant="body-sm"
							sx={{ fontWeight: 700 }}
						>
							This cannot be undone.
						</Typography>
					</Alert>
					<Typography variant="body-sm">
						{
							'By clicking Confirm, you agree that the service above will be cancelled on '
						}
						<Typography
							component="span"
							variant="body-sm"
							sx={{ fontWeight: 500 }}
						>
							{dayjs().format(dateMonthYear)}
						</Typography>
						. Any billing for this service will be stopped and any other
						services associated with this account will not be affected.
					</Typography>
					<Divider />
					<Stack sx={{ gap: 150, width: '100%' }}>
						<Typography variant="label-lg" sx={{ fontWeight: 500 }}>
							To verify, type DESTROY below
						</Typography>
						<HookedTextField
							size="medium"
							name="verification_text"
							autoComplete="off"
							fullWidth
						/>
					</Stack>
					<DestroyNote>
						Removal may take a few moments, or a few days for dedicated servers.
					</DestroyNote>
				</Stack>
				<Stack sx={{ gap: 100, width: '100%' }}>
					<HookedSubmit
						color="danger"
						size="large"
						fullWidth
						disabled={formValues.verification_text !== 'DESTROY'}
						loading={isLoading}
					>
						Destroy service now
					</HookedSubmit>
					<Button
						variant="secondary"
						size="large"
						fullWidth
						startIcon={<ArrowLeftIcon />}
						onClick={onBack}
						disabled={isLoading}
					>
						Back
					</Button>
				</Stack>
			</Stack>
		</LWForm>
	);
};

export default DestroyConfirmation;
